/**
 * Dynamik der Wiedergabe: Zuordnung von Schlagart zu Lautstärkefaktor.
 *
 * Rein funktional und ohne Browser-Bezug. Die Werte sind bewusst einfach
 * gehalten und begrenzt, damit die Summe gleichzeitiger Schläge nicht
 * unkontrolliert übersteuert (zusätzlich begrenzt der Web-Audio-Adapter den
 * Summenpegel).
 *
 * Hinweis zum Modellstand: Das fachliche Modell kennt derzeit ausschließlich
 * `HitType.Normal`. Akzente und Ghost Notes existieren im Modell noch nicht;
 * diese Schicht ist jedoch bereits vollständig darauf vorbereitet und
 * getestet, sodass eine spätere Modellerweiterung ohne Änderung der
 * Audio-Logik hörbar wird.
 */

import { HitType } from '../model/types';

/** Spieldynamik eines einzelnen Schlags. */
export const Dynamic = {
  Normal: 'normal',
  Accent: 'accent',
  Ghost: 'ghost',
} as const;

export type Dynamic = (typeof Dynamic)[keyof typeof Dynamic];

/** Empfohlene Lautstärkefaktoren je Dynamik. */
export const DYNAMIC_GAIN: Readonly<Record<Dynamic, number>> = {
  [Dynamic.Normal]: 1.0,
  [Dynamic.Accent]: 1.25,
  [Dynamic.Ghost]: 0.45,
};

/** Obere Schranke für den Einzelschlag-Pegel (verhindert Übersteuern). */
export const MAX_GAIN = 1.25;

/** Begrenzt einen Lautstärkefaktor auf den zulässigen Bereich. */
export function clampGain(gain: number): number {
  if (!Number.isFinite(gain) || gain < 0) {
    return 0;
  }
  return Math.min(MAX_GAIN, gain);
}

/** Liefert den begrenzten Lautstärkefaktor einer Dynamik. */
export function gainForDynamic(dynamic: Dynamic): number {
  return clampGain(DYNAMIC_GAIN[dynamic]);
}

/**
 * Kennzeichen, aus denen sich eine Dynamik ableitet. Modelliert einen
 * möglichen zukünftigen Zustand, in dem ein Schlag Akzent- und/oder
 * Ghost-Kennzeichen tragen kann.
 */
export interface DynamicFlags {
  readonly accent?: boolean;
  readonly ghost?: boolean;
}

/**
 * Löst Kennzeichen zu genau **einer** Dynamik auf (Prioritätsregel).
 *
 * Prioritätsregel bei gleichzeitigem Akzent und Ghost: **Akzent gewinnt**.
 * Ein Schlag ist damit immer eindeutig genau einer Dynamik zugeordnet; es
 * werden nie zwei Faktoren addiert, wodurch ein unkontrollierter Pegel
 * ausgeschlossen ist.
 */
export function resolveDynamic(flags: DynamicFlags): Dynamic {
  if (flags.accent) {
    return Dynamic.Accent;
  }
  if (flags.ghost) {
    return Dynamic.Ghost;
  }
  return Dynamic.Normal;
}

/**
 * Zuordnung der aktuell im Modell existierenden HitTypes zu einer Dynamik.
 * Wird das Modell später um Akzente/Ghost Notes erweitert, erzwingt der
 * `Record`-Typ die Ergänzung dieser Tabelle.
 */
export const HIT_TYPE_DYNAMIC: Readonly<Record<HitType, Dynamic>> = {
  [HitType.Normal]: Dynamic.Normal,
};

/** Bestimmt die Dynamik eines Modell-Schlags. */
export function dynamicForHitType(type: HitType): Dynamic {
  return HIT_TYPE_DYNAMIC[type] ?? Dynamic.Normal;
}
